/*
 * variant-conditional.js
 * --------------------------------------------------------------
 * 给 <variant-selects> 增加“按上一级选项联动隐藏下级选项”的能力，
 * 同时支持通过 metaobject 规则覆写下级选项的标题文案。
 *
 * 工作机制：
 *   1. 完全基于 product.variants（已写入 <script type="application/json">）
 *      推导每一级的可用值集合，无需任何配置。
 *   2. metaobject 规则只用于覆写 Option2/Option3 的“标题文字”，
 *      不影响联动逻辑。
 *
 * 使用：在需要启用联动的页面，给 <variant-selects> 标签上加
 *   data-conditional-options
 * 并在同一容器内放置：
 *   <script type="application/json"
 *           data-conditional-options-rules>[ ... ]</script>
 *
 * 规则 JSON 形如：
 *   [
 *     { "o1": "Shaver Replacement Head", "o2_label": "颜色" },
 *     { "o1": "Toothbrush Heads",        "o2_label": "类型" }
 *   ]
 *
 * 该模块对原有 variant-selects 完全是“增强”，不修改其内部状态。
 */
(function () {
  if (typeof window === 'undefined') return;

  var ATTR_HIDDEN = 'data-conditional-hidden';
  var READY_FLAG = 'conditionalReady';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function init() {
    var nodes = document.querySelectorAll('variant-selects[data-conditional-options]');
    nodes.forEach(setup);
  }

  function setup(selects) {
    if (selects.dataset[READY_FLAG]) return;
    selects.dataset[READY_FLAG] = '1';

    var variants = readVariants(selects);
    if (!variants || variants.length === 0) return;

    var rules = readRules(selects);

    var run = function () { applyConditional(selects, variants, rules); };

    selects.addEventListener('change', function () {
      // 等 variant-selects 自身的 onVariantChange 跑完，再做联动
      window.requestAnimationFrame(run);
    });

    run();
  }

  function readVariants(selects) {
    // 注意：必须排除规则 script（type 已改为非 application/json，但保险起见多重匹配）
    var script = selects.querySelector(':scope > script[type="application/json"]:not([data-conditional-options-rules])');
    if (!script) script = selects.querySelector('script[type="application/json"]:not([data-conditional-options-rules])');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (e) { return null; }
  }

  function readRules(selects) {
    var script = selects.querySelector('script[data-conditional-options-rules]');
    if (!script) return [];
    var text = (script.textContent || '').trim();
    if (!text) return [];
    try {
      var data = JSON.parse(text);
      return Array.isArray(data) ? data : [];
    } catch (e) { return []; }
  }

  function getFieldsets(selects) {
    return Array.from(selects.querySelectorAll('fieldset.product-form__input'));
  }

  function getSelectedValue(fs) {
    var sel = fs.querySelector('select');
    if (sel) return sel.value;
    var checked = fs.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : undefined;
  }

  function applyConditional(selects, variants, rules) {
    var fieldsets = getFieldsets(selects);
    if (fieldsets.length === 0) return;

    var selected = fieldsets.map(getSelectedValue);

    // 联动隐藏：每一级的允许值集合 = 满足前面所有级别已选值的 variants 的当前级取值集合
    var changed = false;
    fieldsets.forEach(function (fs, idx) {
      if (idx === 0) {
        showAllInFieldset(fs);
        return;
      }
      var allowed = new Set();
      variants.forEach(function (v) {
        for (var i = 0; i < idx; i++) {
          if (v['option' + (i + 1)] !== selected[i]) return;
        }
        allowed.add(v['option' + (idx + 1)]);
      });
      filterFieldset(fs, allowed);

      // 当前已选值已被隐藏 → 切到第一个可用值并触发 change
      if (selected[idx] != null && !allowed.has(selected[idx])) {
        var first = allowed.values().next().value;
        if (first != null && setFieldsetValue(fs, first)) {
          selected[idx] = first;
          changed = true;
        }
      }
    });

    // 标签覆写：由 option1 当前值匹配规则
    applyLabelRules(fieldsets, selected, rules);

    if (changed) {
      // 通知 variant-selects 重新计算 variant
      selects.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function applyLabelRules(fieldsets, selected, rules) {
    var o1 = selected[0];
    var matched = (rules || []).find(function (r) { return r && r.o1 === o1; });

    fieldsets.forEach(function (fs, idx) {
      if (idx === 0) return;
      var labelEl = fs.querySelector('.form__label');
      if (!labelEl) return;

      var optionName = getOptionNameFromFieldset(fs);
      if (!optionName) return;

      var key = 'o' + (idx + 1) + '_label';
      var override = matched && matched[key];
      if (isOptionValueLabel(fs, override)) override = '';
      enforceLabelTitle(labelEl, override || optionName);
    });
  }

  function isOptionValueLabel(fs, label) {
    if (!label) return false;
    var normalized = String(label).trim();
    if (!normalized) return false;

    var values = [];
    var select = fs.querySelector('select');
    if (select) {
      values = Array.from(select.options).map(function (opt) { return opt.value; });
    } else {
      values = Array.from(fs.querySelectorAll('input[type="radio"]')).map(function (input) { return input.value; });
    }

    return values.some(function (value) {
      return String(value).trim() === normalized;
    });
  }

  // 从 fieldset 内的 input/select 反推 Shopify 后台配置的 option.name。
  // dropdown: <select name="options[Color]"> 或 "options[Color--sticky]"
  // block/color: <input name="Color"> 或 "Color--sticky"
  function getOptionNameFromFieldset(fs) {
    if (!fs) return null;

    var select = fs.querySelector('select[name^="options["]');
    if (select && select.name) {
      var m = select.name.match(/options\[([^\]]+)\]/);
      if (m) return stripStickySuffix(m[1]);
    }

    var radio = fs.querySelector('input[type="radio"][name]');
    if (radio && radio.name) return stripStickySuffix(radio.name);

    if (fs.dataset && fs.dataset.name) {
      return stripStickySuffix(fs.dataset.name);
    }

    return null;
  }

  function stripStickySuffix(name) {
    return String(name || '').replace(/--sticky$/, '');
  }

  function enforceLabelTitle(labelEl, titleText) {
    var container = labelEl.querySelector(':scope > .form__label__inner') || labelEl;
    var titleEl = container.querySelector(':scope > [data-cond-title], :scope > [data-option-label-title]');

    var showSelectedValue = !labelEl.closest('.product-form__input--dropdown');

    labelEl.querySelectorAll(':scope > .plug_value').forEach(function (el) {
      el.style.display = 'none';
      el.setAttribute('data-cond-hidden', '1');
    });
    labelEl.querySelectorAll(':scope > .form__label__value').forEach(function (el) {
      el.style.display = showSelectedValue ? '' : 'none';
      el.setAttribute('data-cond-hidden', '1');
    });
    container.querySelectorAll(':scope > [data-cond-title], :scope > [data-option-label-title]').forEach(function (node) {
      if (node !== titleEl) node.remove();
    });

    if (!titleEl) {
      Array.prototype.forEach.call(container.childNodes, function (n) {
        if (n.nodeType === Node.TEXT_NODE) n.nodeValue = '';
      });

      titleEl = document.createElement('b');
      titleEl.setAttribute('data-cond-title', '1');
      titleEl.style.cssText = 'font: inherit; color: inherit; letter-spacing: inherit; text-transform: inherit;';
      container.insertBefore(titleEl, container.firstChild);
    } else {
      titleEl.setAttribute('data-cond-title', '1');
    }

    if (titleEl.textContent !== titleText) {
      titleEl.textContent = titleText;
    }
  }

  function showAllInFieldset(fs) {
    fs.querySelectorAll('[' + ATTR_HIDDEN + ']').forEach(function (el) {
      el.style.display = '';
      el.removeAttribute(ATTR_HIDDEN);
      if (el.tagName === 'INPUT' || el.tagName === 'OPTION') el.disabled = false;
    });
  }

  function filterFieldset(fs, allowed) {
    var select = fs.querySelector('select');
    if (select) {
      Array.from(select.options).forEach(function (opt) {
        toggleEl(opt, allowed.has(opt.value));
      });
      return;
    }
    fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
      var label = fs.querySelector('label[for="' + cssEscape(input.id) + '"]');
      var keep = allowed.has(input.value);
      toggleEl(input, keep);
      if (label) toggleEl(label, keep);
    });
  }

  function toggleEl(el, keep) {
    if (keep) {
      el.style.display = '';
      el.removeAttribute(ATTR_HIDDEN);
      if ('disabled' in el) el.disabled = false;
    } else {
      el.style.display = 'none';
      el.setAttribute(ATTR_HIDDEN, '1');
      if ('disabled' in el) el.disabled = true;
    }
  }

  function setFieldsetValue(fs, value) {
    var select = fs.querySelector('select');
    if (select) {
      if (select.value === value) return false;
      select.value = value;
      return true;
    }
    var radio = fs.querySelector('input[type="radio"][value="' + cssEscape(value) + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      return true;
    }
    return false;
  }

  function cssEscape(s) {
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  ready(init);
})();
